<template>
  <div class="card mb-4">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h6 class="mb-0">
        <i class="bi bi-activity me-2"></i>Activity Timeline
      </h6>
      <button class="btn btn-sm btn-outline-secondary" @click="loadActivityTimeline" :disabled="loading">
        <i class="bi bi-arrow-clockwise" :class="{ 'spin': loading }"></i>
      </button>
    </div>
    <div class="card-body p-0">
      <div v-if="activities.length > 0" class="activity-timeline-container">
        <div class="timeline scrollable-timeline">
          <div v-for="activity in activities" :key="activity.id" class="timeline-item">
            <div class="timeline-marker"></div>
            <div class="timeline-content">
              <div class="d-flex align-items-start">
                <img
                  :src="activity.avatar"
                  :alt="activity.person"
                  class="activity-avatar me-3"
                  @error="onActivityAvatarError($event, activity.person)"
                >
                <div class="flex-grow-1">
                  <p class="activity-text mb-1">
                    <strong>{{ activity.person }}</strong>
                    {{ activity.action }}
                  </p>
                  <small class="text-muted">{{ activity.time }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-muted py-4">
        <i class="bi bi-clock-history fs-1 mb-3 d-block"></i>
        <p>{{ emptyMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import ProfileApiService from '@/services/profile/ProfileApiService'

export default {
  name: 'ActivityTimeline',
  props: {
    emptyMessage: {
      type: String,
      default: 'No recent activity to display'
    }
  },
  data() {
    return {
      activities: [],
      loading: false
    }
  },
  mounted() {
    this.loadActivityTimeline()
  },
  methods: {
    async loadActivityTimeline() {
      try {
        this.loading = true

        const response = await ProfileApiService.get('/my-profile/activity-timeline')

        let activities = []
        if (response && response.Data && response.Data.success && Array.isArray(response.Data.data)) {
          activities = response.Data.data
        } else if (response && response.success && Array.isArray(response.data)) {
          activities = response.data
        } else if (response && Array.isArray(response.data)) {
          activities = response.data
        } else if (Array.isArray(response)) {
          activities = response
        }

        if (activities.length > 0) {
          this.activities = activities.map(activity => ({
            id: activity.id,
            person: activity.user_name,
            action: activity.message,
            avatar: this.getActivityAvatar(activity),
            time: activity.human_time,
            originalActivity: activity
          }))
        } else {
          this.activities = []
        }
      } catch (error) {
        console.error('Error loading activity timeline:', error)
        this.activities = []
      } finally {
        this.loading = false
      }
    },

    onActivityAvatarError(event, userName) {
      const activity = this.activities.find(a => a.person === userName)
      if (activity && activity.originalActivity) {
        const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random`
        event.target.src = defaultAvatar
      } else {
        const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random`
        event.target.src = defaultAvatar
      }
    },

    getActivityAvatar(activity) {
      if (activity.user_avatar) {
        return activity.user_avatar
      }
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(activity.user_name || 'User')}&background=random`
    }
  }
}
</script>

<style scoped>
.activity-timeline-container {
  max-height: 600px;
  overflow-y: auto;
}

.timeline {
  position: relative;
  padding: 0;
}

.timeline-item {
  display: flex;
  padding: 1rem;
  position: relative;
  border-left: 2px solid #e9ecef;
  margin-left: 1rem;
}

.timeline-item:first-child {
  border-left: 2px solid #17a2b8;
}

.timeline-marker {
  width: 12px;
  height: 12px;
  background-color: #17a2b8;
  border-radius: 50%;
  position: absolute;
  left: -7px;
  top: 1.5rem;
}

.timeline-item:first-child .timeline-marker {
  background-color: #17a2b8;
  width: 16px;
  height: 16px;
  left: -9px;
}

.timeline-content {
  flex-grow: 1;
  padding-left: 1rem;
}

.activity-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.activity-text {
  color: #495057;
  font-size: 0.95rem;
  line-height: 1.5;
}

.scrollable-timeline {
  max-height: 400px;
  overflow-y: auto;
}

.btn-outline-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
